import { inject, Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpStatusCode,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of, throwError, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { ErrorLocationEnum } from '../enums/errorLocation.enum';
import { APP_CONFIG } from '../../app.tokens';
import { Router } from '@angular/router';
import { isNullOrUndefined } from 'is-what';

/**
 * Intercepts all outgoing HTTP requests to provide centralized error handling
 * and request cancellation logic for the application.
 *
 * Behavior summary:
 * - Silently short-circuits subsequent requests once a 403 (Forbidden) has been encountered
 *   (prevents request storms when the user loses authorization).
 * - Cancels all in‑flight requests upon the first 403 and navigates to the /unauthorized route.
 * - Swallows (returns empty HttpResponse) for transient / ignorable statuses:
 *   - 422 UnprocessableEntity (validation or domain errors handled elsewhere)
 *   - 0   (network interruption / CORS / offline)
 *   - 502 BadGateway
 *   - 503 ServiceUnavailable
 * - Optionally ignores 404 responses for configured URL substrings (config.ignore404Urls).
 * - Converts other errors into a structured Error object with:
 *   - Combined status, message, and (when derivable) the last URL path segment ("Widget")
 *   - A custom cause flag (ErrorLocationEnum.Server)
 *
 * Properties:
 * - forbiddenEncountered: Tracks whether a 403 has already occurred; once true, all future
 *   intercepted requests are short‑circuited with an empty HttpResponse.
 * - cancelAll$: Subject used with takeUntil() to cancel active request streams after a 403.
 * - config: Injected application configuration (must expose ignore404Urls: string[]).
 * - router: Angular Router used to redirect to '/unauthorized' on 403.
 *
 * Method:
 * - intercept(request, next): Applies the logic above, returning either the normal flow of
 *   HttpEvents, silent empty responses for ignorable cases, or an error Observable that emits
 *   a wrapped Error containing diagnostic information.
 *
 * Error message format (non-ignored server errors):
 *   "Server error
 *    Error Code: <status>
 *    Message: <resolved message> [Widget: <lastSegment>]"
 *
 * Usage:
 * Register this class in the HTTP_INTERCEPTORS provider array (multi: true) so that
 * all HttpClient calls automatically receive the described error behavior.
 *
 * Notes:
 * - Completing cancelAll$ after a 403 ensures takeUntil unsubscribes existing pipelines.
 * - The short‑circuit HttpResponse objects intentionally omit bodies to signal "handled."
 * - Adjust the ignorable status codes or 404 URL patterns via configuration as needed.
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private forbiddenEncountered = false;
  private cancelAll$ = new Subject<void>();

  config = inject(APP_CONFIG);
  private router = inject(Router);
  intercept(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    request: HttpRequest<any>,
    next: HttpHandler,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Observable<HttpEvent<any>> {
    if (this.forbiddenEncountered) {
      return of(new HttpResponse({ body: null }));
    }

    return next.handle(request).pipe(
      takeUntil(this.cancelAll$),
      catchError((error: HttpErrorResponse) => {
        if (
          error.status === HttpStatusCode.UnprocessableEntity ||
          error.status === 0 ||
          error.status === HttpStatusCode.BadGateway ||
          error.status === HttpStatusCode.ServiceUnavailable
        ) {
          return of(new HttpResponse());
        }
        if (this.forbiddenEncountered) {
          return of(new HttpResponse({ body: null }));
        }
        if (
          error.status === HttpStatusCode.NotFound &&
          this.config.ignore404Urls.some((url) => request.url.includes(url))
        ) {
          return of(new HttpResponse({ body: null }));
        }

        if (error.status === HttpStatusCode.Forbidden) {
          this.forbiddenEncountered = true;
          this.cancelAll$.next();
          this.cancelAll$.complete();
          this.router.navigate(['/unauthorized']);
          return of(new HttpResponse({ body: null }));
        }

        let errorMessage = '';
        if (error.error instanceof ErrorEvent) {
          errorMessage = `Server error: ${error.error.message}`;
        } else {
          let errorMsg = error.message;
          if (!isNullOrUndefined(error.error.message)) {
            errorMsg = error.error.message;
          }
          const lastUrlSegment = error.url
            ? (error.url.split('?')[0].split('/').filter(Boolean).pop() ?? '')
            : '';

          const widgetmessage =
            lastUrlSegment !== '' ? `\r\nWidget: ${lastUrlSegment}` : '';
          errorMessage = `Server error\r\nError Code: ${error.status}\r\nMessage: ${errorMsg} ${widgetmessage}`;
        }
        return throwError(
          () => new Error(errorMessage, { cause: ErrorLocationEnum.Server }),
        );
      }),
    );
  }
}
